export type EnvironmentType = 'dev' | 'staging' | 'prod' | 'local' | 'unknown';
export type RegionCode = 'us-east-1' | 'eu-north-1' | 'unknown';

export interface ParsedEnvironment {
  raw: string;
  environment: EnvironmentType;
  region: RegionCode;
}

const ENV_PREFIX_MAP: Record<string, EnvironmentType> = {
  dv: 'dev',
  sg: 'staging',
  pr: 'prod'
};

const REGION_SUFFIX_MAP: Record<string, RegionCode> = {
  '50': 'us-east-1',
  '53': 'eu-north-1'
};

/**
 * Parses a Cvent environment code into its environment and region components.
 * Format: "public-" prefix + 2-char environment prefix + 2-digit region suffix (e.g., "public-dv50", "public-pr53").
 * Prefixes: dv=dev, sg=staging, pr=prod. Suffixes: 50=us-east-1, 53=eu-north-1.
 * Special case: "local" or empty string maps to local/unknown.
 */
export function parseEnvironment(cventEnv: string): ParsedEnvironment {
  if (!cventEnv || cventEnv === 'local') {
    return { raw: cventEnv || '', environment: 'local', region: 'unknown' };
  }

  const normalized = cventEnv.replace(/^public-/, '');
  const prefix = normalized.substring(0, 2);
  const suffix = normalized.substring(2);

  return {
    raw: cventEnv,
    environment: ENV_PREFIX_MAP[prefix] ?? 'unknown',
    region: REGION_SUFFIX_MAP[suffix] ?? 'unknown'
  };
}

export function isProd(env: ParsedEnvironment): boolean {
  return env.environment === 'prod';
}

export function isDev(env: ParsedEnvironment): boolean {
  return env.environment === 'dev' || env.environment === 'local';
}

export function isStaging(env: ParsedEnvironment): boolean {
  return env.environment === 'staging';
}

export function isUS(env: ParsedEnvironment): boolean {
  return env.region === 'us-east-1';
}

export function isEU(env: ParsedEnvironment): boolean {
  return env.region === 'eu-north-1';
}
